import React, { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Save, Image, ShieldAlert, Building2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const DEFAULT_FORM = {
  razao_social: "",
  cnpj: "",
  endereco: "",
  cep: "",
  telefone: "",
  email: "",
  site: "",
  logo_url: null,
  dados_bancarios: {
    pix: "", pix_tipo: "celular", titular: "", banco: "", agencia: "", conta: "", cnpj: "",
  },
  estados_atuacao: [],
  clientes_referencia: [],
};

export default function ConfiguracoesEmpresa({ canEdit = true }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [novoEstado, setNovoEstado] = useState("");
  const [novoCliente, setNovoCliente] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/configuracoes/empresa")
      .then(({ data }) => setForm({ ...DEFAULT_FORM, ...data, dados_bancarios: { ...DEFAULT_FORM.dados_bancarios, ...(data.dados_bancarios || {}) } }))
      .catch(() => toast.error("Falha ao carregar configurações da empresa"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      await api.put("/configuracoes/empresa", form);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/configuracoes/empresa/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, logo_url: data.logo_url });
      toast.success("Logo enviada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    }
  };

  if (loading) {
    return <div className="py-10 text-center"><Loader2 className="inline animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="config-empresa-page">
      {!canEdit && (
        <div className="flex gap-2 items-start p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          <span>Somente Gerente e Diretor podem editar. Você está em modo leitura.</span>
        </div>
      )}

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2 border-b pb-2">
          <Building2 size={18} style={{ color: "#1B3A6B" }} />
          <h3 className="text-lg font-semibold">Dados da Empresa</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Razão social</Label>
            <Input
              data-testid="empresa-razao"
              value={form.razao_social}
              onChange={(e) => setForm({ ...form, razao_social: e.target.value })}
              disabled={!canEdit}
            />
          </div>
          <div>
            <Label>CNPJ</Label>
            <Input data-testid="empresa-cnpj" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} disabled={!canEdit} />
          </div>
          <div>
            <Label>CEP</Label>
            <Input data-testid="empresa-cep" value={form.cep} onChange={(e) => setForm({ ...form, cep: e.target.value })} disabled={!canEdit} />
          </div>
          <div className="md:col-span-2">
            <Label>Endereço completo</Label>
            <Input data-testid="empresa-endereco" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} disabled={!canEdit} />
          </div>
          <div>
            <Label>Telefone</Label>
            <Input data-testid="empresa-telefone" value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: e.target.value })} disabled={!canEdit} />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input data-testid="empresa-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!canEdit} />
          </div>
        </div>

        <div className="border-t pt-3">
          <Label className="text-xs">Logo (PNG/JPG/SVG até 5MB)</Label>
          <div className="flex items-center gap-4 mt-2">
            {form.logo_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ""}${form.logo_url}?t=${Date.now()}`}
                alt="logo"
                className="h-16 max-w-[200px] object-contain border rounded p-2 bg-white"
              />
            ) : (
              <div className="h-16 w-32 border-2 border-dashed rounded flex items-center justify-center text-xs text-muted-foreground">
                <Image size={20} className="mr-1" /> Sem logo
              </div>
            )}
            {canEdit && (
              <input
                data-testid="empresa-logo-input"
                type="file"
                accept="image/*"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                className="text-xs"
              />
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="border-b pb-2"><h3 className="text-lg font-semibold">Dados Bancários (saem no PDF)</h3></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>PIX</Label>
            <Input data-testid="emp-pix" value={form.dados_bancarios.pix || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, pix: e.target.value } })} disabled={!canEdit} />
          </div>
          <div>
            <Label>Tipo PIX</Label>
            <select
              data-testid="emp-pix-tipo"
              value={form.dados_bancarios.pix_tipo || "celular"}
              onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, pix_tipo: e.target.value } })}
              disabled={!canEdit}
              className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
            >
              <option value="celular">Celular</option>
              <option value="cnpj">CNPJ</option>
              <option value="email">E-mail</option>
              <option value="aleatoria">Chave aleatória</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Titular</Label>
            <Input data-testid="emp-titular" value={form.dados_bancarios.titular || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, titular: e.target.value } })} disabled={!canEdit} />
          </div>
          <div>
            <Label>Banco</Label>
            <Input data-testid="emp-banco" value={form.dados_bancarios.banco || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, banco: e.target.value } })} disabled={!canEdit} />
          </div>
          <div>
            <Label>CNPJ</Label>
            <Input value={form.dados_bancarios.cnpj || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, cnpj: e.target.value } })} disabled={!canEdit} />
          </div>
          <div>
            <Label>Agência</Label>
            <Input data-testid="emp-ag" value={form.dados_bancarios.agencia || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, agencia: e.target.value } })} disabled={!canEdit} />
          </div>
          <div>
            <Label>Conta</Label>
            <Input data-testid="emp-cc" value={form.dados_bancarios.conta || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, conta: e.target.value } })} disabled={!canEdit} />
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6 space-y-3">
          <h3 className="text-lg font-semibold border-b pb-2">Estados de Atuação</h3>
          <p className="text-xs text-muted-foreground">Saem no PDF como "Área de Atuação Locativo".</p>
          {canEdit && (
            <div className="flex gap-2">
              <Input data-testid="emp-novo-estado" placeholder="Ex: Pernambuco - PE" value={novoEstado} onChange={(e) => setNovoEstado(e.target.value)} />
              <Button onClick={() => { if (!novoEstado.trim()) return; setForm({ ...form, estados_atuacao: [...form.estados_atuacao, novoEstado.trim()] }); setNovoEstado(""); }}>
                <Plus size={14} />
              </Button>
            </div>
          )}
          <ul className="space-y-1">
            {form.estados_atuacao.map((e, i) => (
              <li key={i} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-1.5 rounded" data-testid={`emp-estado-${i}`}>
                <span>{e}</span>
                {canEdit && (
                  <button onClick={() => setForm({ ...form, estados_atuacao: form.estados_atuacao.filter((_, idx) => idx !== i) })} className="text-red-500 hover:text-red-700">
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            ))}
            {form.estados_atuacao.length === 0 && <li className="text-xs text-muted-foreground italic">Nenhum estado cadastrado</li>}
          </ul>
        </Card>

        <Card className="p-6 space-y-3">
          <h3 className="text-lg font-semibold border-b pb-2">Clientes de Referência</h3>
          <p className="text-xs text-muted-foreground">Empresas que aparecem na seção "Alguns de nossos clientes" do PDF.</p>
          {canEdit && (
            <div className="flex gap-2">
              <Input data-testid="emp-novo-cliente" placeholder="Ex: Supermercado Modelo Ltda" value={novoCliente} onChange={(e) => setNovoCliente(e.target.value)} />
              <Button onClick={() => { if (!novoCliente.trim()) return; setForm({ ...form, clientes_referencia: [...form.clientes_referencia, novoCliente.trim()] }); setNovoCliente(""); }}>
                <Plus size={14} />
              </Button>
            </div>
          )}
          <ul className="space-y-1 max-h-[260px] overflow-auto">
            {form.clientes_referencia.map((c, i) => (
              <li key={i} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-1.5 rounded" data-testid={`emp-cliente-${i}`}>
                <span>{c}</span>
                {canEdit && (
                  <button onClick={() => setForm({ ...form, clientes_referencia: form.clientes_referencia.filter((_, idx) => idx !== i) })} className="text-red-500 hover:text-red-700">
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            ))}
            {form.clientes_referencia.length === 0 && <li className="text-xs text-muted-foreground italic">Nenhum cliente cadastrado</li>}
          </ul>
        </Card>
      </div>

      {canEdit && (
        <div className="flex justify-end sticky bottom-4">
          <Button onClick={salvar} disabled={saving} size="lg" style={{ background: "#1B3A6B", color: "white" }} data-testid="empresa-salvar">
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save size={14} className="mr-2" />}
            Salvar configurações
          </Button>
        </div>
      )}
    </div>
  );
}
